import type { useCapacityStep } from './CapacityStep/useCapacityStep';
import type { useOpportunityStep } from './PrerequisitesStep/useOpportunityStep';
import type { usePropertyStep } from './PropertyStep/usePropertyStep';
import type { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import type { useRoomRatesStep } from './RoomRatesStep/useRoomRatesStep';
import type { useRoomsStep } from './RoomsStep/useRoomsStep';
import type { useSubmissionStep } from './SubmissionStep/useSubmissionStep';
import { getActiveStepFlags } from './activeStepFlags';
import { getNextButtonLabel } from './utils';

export interface UseWizardButtonStateParams {
  activeStep: number;
  capacityStep: ReturnType<typeof useCapacityStep>;
  isAdvancingStep: boolean;
  opportunity: ReturnType<typeof useOpportunityStep>;
  propertyStep: ReturnType<typeof usePropertyStep>;
  ratePlanStep: ReturnType<typeof useRatePlanStep>;
  roomRatesStep: ReturnType<typeof useRoomRatesStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  submissionStep: ReturnType<typeof useSubmissionStep>;
}

/**
 * Owns whether the wizard is "busy" (which disables Next/Close across every step) and what the
 * primary action button should say, both of which are simple aggregates over every step hook's
 * own in-flight/label state rather than logic belonging to any single step.
 */
export function useWizardButtonState({
  activeStep,
  capacityStep,
  isAdvancingStep,
  opportunity,
  propertyStep,
  ratePlanStep,
  roomRatesStep,
  roomsStep,
  submissionStep,
}: UseWizardButtonStateParams) {
  const { isSubmissionStep } = getActiveStepFlags(activeStep);

  const isStepBusy =
    isAdvancingStep ||
    opportunity.isValidatingOpportunity ||
    propertyStep.isCreatingNewProperty ||
    roomsStep.isCreatingRooms ||
    ratePlanStep.isCreatingRatePlan ||
    roomRatesStep.isCreatingRoomRates ||
    capacityStep.isApplyingDefaultCapacity ||
    submissionStep.isSubmitting;

  // Room/rate-plan/room-rate creation only happens for real at Submission now, so their
  // corresponding "Create N rooms..."/"Applying..." labels don't apply until then -- every
  // drafting step's Next just reads "Next" (or "Preparing rooms..." during the Property→Rooms
  // draft-matching handoff, which is unrelated to real creation).
  const nextButtonLabel = getNextButtonLabel({
    isAdvancingStep,
    isCreatePropertyStep: propertyStep.isCreatePropertyStep,
    isCreatingNewProperty: propertyStep.isCreatingNewProperty,
    isValidatingOpportunity: opportunity.isValidatingOpportunity,
  });
  const primaryButtonLabel = isSubmissionStep
    ? submissionStep.isSubmitting
      ? 'Creating Flash offer...'
      : 'Create Flash offer'
    : nextButtonLabel;

  return { isStepBusy, primaryButtonLabel };
}
